import { API_URL as API, BACKEND_URL } from '../utils/config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';


const Admin = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name_en: '',
    name_no: '',
    description_en: '',
    description_no: '',
    category: 'foundation',
    price_nok: '',
    image_url: '',
    shades: [],
    in_stock: true,
    featured: false
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchProducts();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        price_nok: parseFloat(formData.price_nok)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, payload, { headers });
        toast.success('Product created successfully');
      }

      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name_en: product.name_en,
      name_no: product.name_no,
      description_en: product.description_en,
      description_no: product.description_no,
      category: product.category,
      price_nok: product.price_nok.toString(),
      image_url: product.image_url,
      shades: product.shades || [],
      in_stock: product.in_stock,
      featured: product.featured
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/products/${productId}`, { headers });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name_en: '',
      name_no: '',
      description_en: '',
      description_no: '',
      category: 'foundation',
      price_nok: '',
      image_url: '',
      shades: [],
      in_stock: true,
      featured: false
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center">
        <p className="text-[#8A8A8A]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-lg font-sans font-bold uppercase tracking-tight text-[#1A1A1A]">
            Admin Panel
          </h1>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/newsletter')}
              className="bg-purple-600 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-purple-700 transition-all"
            >
              Newsletter
            </button>
            <button
              onClick={() => navigate('/admin/orders')}
              className="bg-blue-600 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-blue-700 transition-all"
            >
              View Orders
            </button>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-[#1A1A1A] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all"
              >
                Add New Product
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div className="bg-white p-8 rounded-lg shadow-md mb-8">
            <h2 className="text-2xl font-sans mb-6 text-[#1A1A1A]">
              {editingProduct ? 'Edit Product' : 'New Product'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#1A1A1A] mb-2">Name (English)</label>
                  <input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#1A1A1A] mb-2">Name (Norwegian)</label>
                  <input
                    type="text"
                    name="name_no"
                    value={formData.name_no}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#1A1A1A] mb-2">Description (English)</label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                />
              </div>

              <div>
                <label className="block text-sm text-[#1A1A1A] mb-2">Description (Norwegian)</label>
                <textarea
                  name="description_no"
                  value={formData.description_no}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm text-[#1A1A1A] mb-2">Category</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#1A1A1A] mb-2">Price (NOK)</label>
                  <input
                    type="number"
                    name="price_nok"
                    value={formData.price_nok}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#1A1A1A] mb-2">Image URL</label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-2 text-sm focus:outline-none focus:border-[#1A1A1A]"
                  />
                </div>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="in_stock"
                    checked={formData.in_stock}
                    onChange={handleInputChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-[#1A1A1A]">In Stock</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-[#1A1A1A]">Featured</span>
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-[#1A1A1A] text-white px-8 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-transparent border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#1A1A1A] text-white">
              <tr>
                <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Product</th>
                <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Category</th>
                <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Price</th>
                <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Stock</th>
                <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F7]'}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img src={product.image_url} alt={product.name_en} className="w-12 h-12 object-contain" />
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">{product.name_en}</p>
                        <p className="text-xs text-[#8A8A8A]">{product.name_no}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#1A1A1A]">{product.category}</td>
                  <td className="px-6 py-4 text-sm text-[#1A1A1A]">{product.price_nok} NOK</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs uppercase tracking-[0.1em] ${product.in_stock ? 'text-green-600' : 'text-red-600'}`}>
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-xs uppercase tracking-[0.1em] text-[#1A1A1A] hover:text-[#C6A87C] transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-xs uppercase tracking-[0.1em] text-red-600 hover:text-red-800 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
